/**
 * The Macro layout's shared cursor: the week one section is standing on, and
 * whether it is playing, for every other section on the layout to read
 * (`docs/plans/macro.md`, *one cursor*).
 *
 * **One writer.** The `yieldCurve` section owns the clock — its cursor, ▶ Play
 * and the scrub — and publishes here through {@link usePublishMacroCursor}.
 * Every other section only *reads*, through {@link useMacroCursor}: a second
 * writer would make two clocks race for one value, which is the bug a shared
 * cursor exists to remove. A reader that wants to follow the curve draws the
 * week it is given; one that does not ignores it.
 *
 * **Layout scoped.** `SectionGrid` mounts one {@link MacroCursorProvider}
 * around the layout on screen, so the cursor reaches every section of that
 * layout and nothing outside it. It is not keyed on the layout (a key would
 * remount every chart on a tab switch); the writer clears the cursor as it
 * unmounts, so a layout without a yieldCurve section reads {@link NO_CURSOR}.
 * Outside a provider — a section rendered on its own in a test or a
 * harness — the hooks fall back to one module-level store, so they still work.
 *
 * View state (D8): never stored, never sent.
 *
 * | field | meaning |
 * |---|---|
 * | `week` | the cursor's week, epoch ms UTC (the curve row's date), `null` before the curve loads |
 * | `playing` | ▶ Play is running — a reader may skip expensive work until it stops |
 * | `owner` | the publishing section's id, so a reader can tell a cursor from its own |
 */

import { createContext, createElement, useCallback, useContext, useEffect, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";

export interface MacroCursor {
  week: number | null;
  playing: boolean;
  owner: string | null;
}

export const NO_CURSOR: MacroCursor = Object.freeze({ week: null, playing: false, owner: null });

export interface MacroCursorStore {
  get(): MacroCursor;
  set(next: MacroCursor): void;
  subscribe(listener: () => void): () => void;
}

export function createMacroCursorStore(): MacroCursorStore {
  let value: MacroCursor = NO_CURSOR;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      if (next.week === value.week && next.playing === value.playing && next.owner === value.owner) return;
      value = next;
      for (const l of [...listeners]) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const fallback = createMacroCursorStore();
const Ctx = createContext<MacroCursorStore>(fallback);

/** One cursor per layout: mount it once around a layout's sections. */
export function MacroCursorProvider({ children }: { children?: ReactNode }) {
  const [store] = useState(createMacroCursorStore);
  return createElement(Ctx.Provider, { value: store }, children);
}

/** Read the layout's cursor; re-renders when it moves. */
export function useMacroCursor(): MacroCursor {
  const store = useContext(Ctx);
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

/**
 * Publish `week` and `playing` as section `owner`'s cursor, and clear it on
 * unmount if it is still this section's. Only the `yieldCurve` section calls
 * this (see the module note: one writer).
 */
export function usePublishMacroCursor(owner: string, week: number | null, playing: boolean): void {
  const store = useContext(Ctx);
  useEffect(() => {
    store.set({ week, playing, owner });
  }, [store, owner, week, playing]);
  const clear = useCallback(() => {
    if (store.get().owner === owner) store.set(NO_CURSOR);
  }, [store, owner]);
  useEffect(() => clear, [clear]);
}
